import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { getErpErrorMessage } from 'service/ErpService';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Plus, Pencil, Search, UsersRound, LoaderCircle, UserRound, CircleCheck, CircleAlert, RefreshCw, ChevronLeft, ChevronRight, X, Save } from "lucide-react";
import { getErpUsers, createErpUser, updateErpUser, checkErpInn } from "service/ErpService";
import { getRoleLabel } from "utils/access";
import { StatusBadge } from "components/Erp/Display";
import { SectionHeading, EmptyState } from "components/Erp/Presentation";
import AuditButton from "./AuditButton";
import UserAccessEditor, { ACCESS_FIELDS } from "./UserAccessEditor";
import a from "./UserAccessEditor.module.scss";
import s from "../Management.module.scss";

export { ACCESS_FIELDS } from "./UserAccessEditor";
const blank = () => ({
  username: "",
  password: "",
  first_name: "",
  surname: "",
  last_name: "",
  number_phone: "",
  email: "",
  is_active: true,
  is_superuser: false,
  is_report_of: false,
  erp_access: Object.fromEntries(ACCESS_FIELDS.map(([key]) => [key, false])),
});
const errorText = (error) => {
  return getErpErrorMessage(error,tr("Не удалось сохранить. Проверьте соединение и повторите."));
};

export default function UsersPanel() {
  useInterfaceLanguage();
  const user = useSelector((state) => state.user);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [innValidation, setInnValidation] = useState({ status: "idle", message: "", inn: "" });
  const [innRetry, setInnRetry] = useState(0);
  const innRequest = useRef(0);
  useEffect(() => {
    const version = ++innRequest.current;
    const inn = form.username;
    if (!editing || editing.id || !/^\d{14}$/.test(inn)) {
      setInnValidation({ status: "idle", message: "", inn });
      return;
    }
    setInnValidation({ status: "checking", message: tr("Проверяем ИНН в базе…"), inn });
    const timer = setTimeout(async () => {
      try {
        const { data } = await checkErpInn(inn);
        if (version !== innRequest.current) return;
        if (typeof data.available !== "boolean") throw new Error("Invalid availability response");
        setInnValidation({ status: data.available ? "available" : "taken", message: data.detail || (data.available ? tr("ИНН свободен. Можно создать пользователя.") : tr("Этот ИНН уже есть в базе. Новый пользователь не будет создан.")), inn });
      } catch (e) {
        if (version === innRequest.current) setInnValidation({ status: "error", message: tr("Не удалось проверить ИНН. Повторите проверку."), inn });
      }
    }, 350);
    return () => { clearTimeout(timer); innRequest.current = version + 1; };
  }, [editing, form.username, innRetry]);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getErpUsers({
        page,
        page_size: 20,
        search: query,
        erp_role: roleFilter || undefined,
        is_active: activeFilter || undefined,
      });
      setRecords(data.results || data || []);
      setTotal(data.count ?? (data.results || data || []).length);
    } catch (e) {
      setError(
        getErpErrorMessage(e, tr("Не удалось загрузить пользователей.")),
      );
    } finally {
      setLoading(false);
    }
  }, [page, query, roleFilter, activeFilter]);
  useEffect(() => {
    load();
  }, [load]);
  const field = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const open = (item) => {
    setError("");
    setSuccess("");
    setEditing(item || {});
    setForm(
      item
        ? {
            ...blank(),
            ...item,
            password: "",
            erp_access: { ...blank().erp_access, ...item.erp_access },
          }
        : blank(),
    );
  };
  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!editing.id && !/^\d{14}$/.test(form.username)) {
      setError(tr("ИНН должен содержать ровно 14 цифр."));
      return;
    }
    setBusy(true);
    setError("");
    const payload = {
      username: form.username,
      first_name: form.first_name.trim(),
      surname: form.surname.trim(),
      last_name: form.last_name.trim(),
      number_phone: form.number_phone.trim(),
      email: form.email.trim(),
    };
    if (form.password) payload.password = form.password;
    if (user.is_superuser)
      Object.assign(payload, {
        is_active: form.is_active,
        is_superuser: form.is_superuser,
        is_report_of: form.is_report_of,
        erp_access: form.erp_access,
      });
    try {
      if (!editing.id) {
        const { data } = await checkErpInn(form.username);
        if (data.available !== true) {
          const message = data.available === false ? (data.detail || tr("Этот ИНН уже есть в базе. Новый пользователь не будет создан.")) : tr("Не удалось проверить ИНН. Повторите проверку.");
          setInnValidation({ status: data.available === false ? "taken" : "error", message, inn: form.username });
          setError(message);
          return;
        }
      }
      if (editing.id) await updateErpUser(editing.id, payload);
      else await createErpUser(payload);
      setEditing(null);
      setSuccess(
        editing.id
          ? tr("Изменения пользователя сохранены.")
          : tr("Пользователь зарегистрирован."),
      );
      await load();
    } catch (e) {
      const message = errorText(e);
      setError(message);
      if (!editing.id && (e?.response?.data?.username || e?.response?.data?.inn)) setInnValidation({ status: "taken", message, inn: form.username });
    } finally {
      setBusy(false);
    }
  };
  const input = (key, label, options = {}) => (
    <label className={s.field}>
      {tr(label)}
      <input
        value={form[key] || ""}
        onChange={(e) => {
          if (key === "username" && !editing?.id) setError("");
          field(
            key,
            key === "username" && !editing?.id
              ? e.target.value.replace(/\D/g, "").slice(0, 14)
              : e.target.value,
          );
        }}
        {...options}
      />
    </label>
  );
  return (
    <section className={s.panel}>
      <SectionHeading icon={UsersRound} title={tr("Пользователи")} description={tr("Учётные записи сотрудников и пользователей отчётов. Водители и операторы находятся в своих разделах.")}
        actions={<button type="button" className={s.primary} onClick={() => open(null)}>
          <Plus size={15} />{tr("Добавить пользователя")}</button>} />
      {tr(!editing && error && (
        <div role="alert" className={s.error}>
          {tr(error)}
          <button className={s.button} onClick={load}><RefreshCw size={14} aria-hidden="true"/>{tr("Повторить")}</button>
        </div>
      ))}
      {tr(success && (
        <div role="status" className={s.success}>
          {tr(success)}
        </div>
      ))}
      <form
        className={s.toolbar}
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setQuery(search.trim());
        }}
      >
        <input
          aria-label={tr("Поиск пользователей")}
          placeholder={tr("ИНН, имя, телефон")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          aria-label={tr("Роль пользователя")}
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
        >
          <option value="">{tr("Все роли")}</option>
          <option value="superuser">{tr("Суперпользователь")}</option>
          <option value="administrator">{tr("Администратор")}</option>
          <option value="reporter">{tr("Только отчёты")}</option>
          <option value="employee">{tr("Сотрудник Кыргызкомур")}</option>
          <option value="user">{tr("Пользователь")}</option>
        </select>
        <select
          aria-label={tr("Активность пользователя")}
          value={activeFilter}
          onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
        >
          <option value="">{tr("Любая активность")}</option>
          <option value="true">{tr("Активные")}</option>
          <option value="false">{tr("Отключённые")}</option>
        </select>
        <button className={s.button}>
          <Search size={15} />{tr("Найти")}</button>
      </form>
      {loading ? (
        <EmptyState icon={LoaderCircle} title={tr("Загрузка пользователей…")} compact />
      ) : !records.length ? (
        <EmptyState icon={UsersRound} title={tr("Пользователи не найдены")} description={tr("Измените фильтры или поиск, чтобы найти нужную учётную запись.")} compact />
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{tr("Пользователь")}</th>
                <th>{tr("ИНН / логин")}</th>
                <th>{tr("Роль")}</th>
                <th>{tr("Контакты")}</th>
                <th>{tr("Доступ")}</th>
                <th>{tr("Действия")}</th>
              </tr>
            </thead>
            <tbody>
              {records.map((item) => (
                <tr key={item.id}>
                  <td data-label={tr("Пользователь")}>
                    <strong>
                      {tr([item.surname, item.first_name, item.last_name]
                        .filter(Boolean)
                        .join(" ") || tr("Без имени"))}
                    </strong>
                  </td>
                  <td data-label={tr("ИНН / логин")}>{tr(item.username)}</td>
                  <td data-label={tr("Роль")}>{getRoleLabel(item)}</td>
                  <td data-label={tr("Контакты")}>
                    {tr(item.number_phone || "—")}
                    <div className={s.muted}>{tr(item.email)}</div>
                  </td>
                  <td data-label={tr("Доступ")}>
                    <StatusBadge tone={item.is_active ? "success" : "danger"}>
                      {item.is_active ? tr("Активен") : tr("Отключён")}
                    </StatusBadge>
                    <div className={s.muted}>
                      {item.is_superuser
                        ? tr("Все разделы")
                        : item.is_report_of
                          ? tr("Только просмотр отчётов")
                          : tr("{{value0}} разрешений", { value0: Object.values(item.erp_access || {}).filter(
                                Boolean,
                              ).length })}
                    </div>
                  </td>
                  <td data-label={tr("Действия")}>
                    <div className={s.actions}>
                      <button className={s.button} onClick={() => open(item)}>
                        <Pencil size={14} />{tr("Редактировать")}</button>
                      <AuditButton actorId={item.user_id} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className={s.pagination}>
        <span>{tr("Всего: ")}{tr(total)}{tr(" · Страница ")}{tr(page)}
        </span>
        <button
          className={s.button}
          disabled={page <= 1 || loading}
          onClick={() => setPage((p) => p - 1)}
        ><ChevronLeft size={14} aria-hidden="true"/>{tr("Назад")}</button>
        <button
          className={s.button}
          disabled={page * 20 >= total || loading}
          onClick={() => setPage((p) => p + 1)}
        ><ChevronRight size={14} aria-hidden="true"/>{tr("Далее")}</button>
      </div>
      {tr(editing && (
        <div
          className={s.overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setEditing(null);
          }}
        >
          <form
            className={`${s.dialog} ${a.userDialog}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-edit-title"
            onSubmit={save}
          >
            <h2 id="user-edit-title">
              <UserRound size={20} aria-hidden="true" />
              {editing.id
                ? tr("Редактирование пользователя")
                : tr("Регистрация пользователя")}
            </h2>
            <div className={a.dialogBody}>
            {tr(error && (
              <div role="alert" className={s.error}>
                {tr(error)}
              </div>
            ))}
            <div className={s.grid}>
              <div className={a.innField}>
                {input("username", tr("ИНН"), {
                  required: true,
                  inputMode: "numeric",
                  pattern: editing.id ? undefined : "[0-9]{14}",
                  maxLength: editing.id ? undefined : 14,
                  readOnly: !!editing.id,
                  disabled: busy,
                  "aria-describedby": !editing.id ? "user-inn-check" : undefined,
                  "aria-invalid": !editing.id && innValidation.status === "taken" ? true : undefined,
                })}
                {tr(!editing.id && <div id="user-inn-check" className={`${a.innStatus} ${innValidation.status === "taken" || innValidation.status === "error" ? a.innError : innValidation.status === "available" ? a.innAvailable : ""}`} role="status">
                  {innValidation.status === "checking" ? <LoaderCircle size={14} className={a.spinner} aria-hidden="true" /> : innValidation.status === "available" ? <CircleCheck size={14} aria-hidden="true" /> : ["taken", "error"].includes(innValidation.status) ? <CircleAlert size={14} aria-hidden="true" /> : null}
                  <span>{tr(innValidation.message || tr("14 цифр. Проверка учитывает все учётные записи и ИНН в базе."))}</span>
                  {tr(innValidation.status === "error" && <button type="button" onClick={() => setInnRetry(value => value + 1)} aria-label={tr("Повторить проверку ИНН")}><RefreshCw size={14} aria-hidden="true" /></button>)}
                </div>)}
              </div>
              {tr(!editing.id &&
                input("password", tr("Первоначальный пароль"), {
                  type: "password",
                  required: true,
                  autoComplete: "new-password",
                  minLength: 8,
                }))}
              {input("surname", tr("Фамилия"), { required: true })}
              {input("first_name", tr("Имя"), { required: true })}
              {input("last_name", tr("Отчество"))}
              {input("number_phone", tr("Телефон"), { type: "tel" })}
              {input("email", tr("Электронная почта"), { type: "email" })}
            </div>
            {user.is_superuser ? (
              <>
                <h3>{tr("Роль и доступ")}</h3>
                <label className={s.field}>{tr("Тип пользователя")}<select
                    value={
                      form.is_superuser
                        ? "superuser"
                        : form.is_report_of
                          ? "reporter"
                          : "user"
                    }
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        is_superuser: e.target.value === "superuser",
                        is_report_of: e.target.value === "reporter",
                      }))
                    }
                  >
                    <option value="user">{tr("Пользователь с выбранными разрешениями")}</option>
                    <option value="reporter">{tr("Просмотр отчётов без возможности изменений")}</option>
                    <option value="superuser">{tr("Суперпользователь — полный доступ")}</option>
                  </select>
                </label>
                <p className={s.muted}>
                  {form.is_superuser ? tr("У суперпользователя автоматически открыты все разделы и действия.") : form.is_report_of ? tr("Открыт только просмотр отчётов. Действия изменения недоступны.") : tr("Выберите разделы и действия. Просмотр, редактирование, сброс PIN и блокировка водителей задаются независимо.")}
                </p>
                <UserAccessEditor grants={form.erp_access} isSuperuser={form.is_superuser} isReportOnly={form.is_report_of} onChange={(key, checked) => setForm(prev => ({ ...prev, erp_access: { ...prev.erp_access, [key]: checked } }))} />
                <label>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => field("is_active", e.target.checked)}
                  />{" "}{tr("Учётная запись активна")}</label>
              </>
            ) : (
              <p className={s.muted}>{tr("Права доступа назначает суперпользователь. Новая учётная запись создаётся без разрешений.")}</p>
            )}
            </div>
            <div className={s.footer}>
              <button
                type="button"
                className={s.button}
                disabled={busy}
                onClick={() => setEditing(null)}
              ><X size={14} aria-hidden="true"/>{tr("Отмена")}</button>
              <button className={s.primary} disabled={busy || (!editing.id && ["checking", "taken"].includes(innValidation.status))}><Save size={14} aria-hidden="true"/> 
                {busy ? tr("Сохранение…") : tr("Сохранить")}
              </button>
            </div>
          </form>
        </div>
      ))}
    </section>
  );
}

